/**
 * Comercial → Leads — v2.
 *
 * Mesmo padrão visual/estrutural da tela de Clientes (Clientes.jsx): cinco
 * indicadores no topo, cartão principal (filtros · tabela/cards · paginação)
 * e coluna "Visão dos leads" (donut de saúde, distribuição por origem, quem
 * precisa de atenção). Reusa clientes.css (prefixo `cl-`) — mesma folha de
 * estilo, sem duplicar CSS.
 *
 * Lead é um `contact` (mesma tabela/endpoints do Cliente) ainda sem negócio
 * ganho. A lista não filtra por status no backend (mesmo escopo do Leads.jsx
 * anterior: todo `contact` aparece aqui) — os filtros da barra é que recortam
 * a visão.
 *
 * O que a tela deriva no lead (o backend não guarda esses campos):
 *   - saúde: dias desde `updated_at` (proxy de última interação — a mesma
 *     regra usada em Clientes, mas sem o campo dedicado que só a query de
 *     /crm/clientes calcula) — ≤30 saudável, ≤60 atenção, ≤90 em risco, acima
 *     inativo;
 *   - próxima ação: sugestão de cadência a partir da saúde (não é agenda);
 *   - origem: usada como "segmento" na coluna lateral (Leads por origem);
 *   - responsável: nome resolvido via getCrmMembers (a listagem de contacts
 *     só traz owner_id).
 *
 * Editar/Novo lead usam o mesmo wizard de Clientes (ClienteFormPanel,
 * `entityLabel="lead"`) — zero duplicação de formulário. Clicar numa linha
 * navega para a página cheia do perfil (`/comercial/leads/:id`, LeadView),
 * espelhando ClienteView.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import ClienteFormPanel from './ClienteFormPanel.jsx'
import DealModal from './DealModal.jsx'
import LeadCustomFieldsManager from '../../components/LeadCustomFieldsManager.jsx'
import { getCrmContacts, getCrmMembers, getPipelines, getStages } from '../../db/api.js'
import './clientes.css'

const DIA = 86400000

const brl0 = (v) => `R$ ${Math.round(Number(v) || 0).toLocaleString('pt-BR')}`
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)

const fmtDate = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d)) return '—'
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const diasDesde = (v) => {
  if (!v) return null
  const d = new Date(v)
  if (isNaN(d)) return null
  return Math.floor((Date.now() - d.getTime()) / DIA)
}

const desde = (v) => {
  const dias = diasDesde(v)
  if (dias == null) return 'sem registro'
  if (dias <= 0) return 'hoje'
  if (dias === 1) return 'ontem'
  if (dias < 30) return `há ${dias} dias`
  const meses = Math.round(dias / 30)
  return meses <= 1 ? 'há 1 mês' : `há ${meses} meses`
}

// ── Saúde do lead (derivada de updated_at, proxy de última interação) ──────
const SAUDES = ['saudavel', 'atencao', 'risco', 'inativo']
const SAUDE = {
  saudavel: { label: 'Saudável', cor: '#22c58c' },
  atencao: { label: 'Atenção', cor: '#ffc83d' },
  risco: { label: 'Em risco', cor: '#f9544c' },
  inativo: { label: 'Inativo', cor: '#d5dae2' },
}

function saudeDe(c) {
  const dias = diasDesde(c.updated_at)
  if (dias == null || dias > 90) return 'inativo'
  if (dias <= 30) return 'saudavel'
  if (dias <= 60) return 'atencao'
  return 'risco'
}

const CADENCIA = {
  saudavel: { label: 'Revisão trimestral', dias: 90 },
  atencao: { label: 'Follow-up', dias: 7 },
  risco: { label: 'Contato urgente', dias: 2 },
  inativo: { label: 'Reativação', dias: 0 },
}

function proximaAcao(c, saude) {
  const { label, dias } = CADENCIA[saude]
  const base = c.updated_at ? new Date(c.updated_at) : null
  if (!base || isNaN(base) || saude === 'inativo') return { label, data: new Date() }
  return { label, data: new Date(base.getTime() + dias * DIA) }
}

function motivoAtencao(c, saude) {
  const dias = diasDesde(c.updated_at)
  if (saude === 'inativo') return dias == null ? 'Sem interação registrada' : `Sem interação há ${dias} dias`
  if (saude === 'risco') return 'Risco de perda'
  return 'Baixa interação'
}

const FAIXAS_ORIGEM = [
  ['saudavel', '#22c58c'],
  ['atencao', '#ffc83d'],
  ['risco', '#f9544c'],
  ['inativo', '#d5dae2'],
]

const SEM_ORIGEM = 'Sem origem'
const origemDe = (c) => c.origem || SEM_ORIGEM

const iniciais = (nome) => String(nome || '?')
  .trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?'

const AVATAR_CORES = ['#101828', '#2563eb', '#101828', '#101828']
function corAvatar(id) {
  const s = String(id || '')
  let h = 0
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AVATAR_CORES[h % AVATAR_CORES.length]
}

function listaPaginas(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  if (page <= 4) return [1, 2, 3, 4, 5, '…', total]
  if (page >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total]
  return [1, '…', page - 1, page, page + 1, '…', total]
}

const mesDe = (v) => {
  const d = v ? new Date(v) : null
  return d && !isNaN(d) ? { ano: d.getFullYear(), mes: d.getMonth() } : null
}

const variacao = (atual, anterior) => (anterior > 0 ? ((atual - anterior) / anterior) * 100 : null)

const ORDENACOES = {
  interacao: (c) => new Date(c.updated_at || 0).getTime(),
  nome: (c) => (c.nome || '').toLowerCase(),
  empresa: (c) => (c.company_nome || c.empresa || '').toLowerCase(),
  responsavel: (c) => (c._owner || '').toLowerCase(),
  valor: (c) => num(c.valor_venda),
  saude: (c) => SAUDES.indexOf(c._saude),
}

// ── Peças ───────────────────────────────────────────────────────────────────

function Kpi({ icon, cor, bg, label, value, foot, footTone, active, onClick }) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={`cl-kpi${onClick ? '' : ' static'}${active ? ' active' : ''}`}
      onClick={onClick}
      title={onClick ? `Filtrar: ${label}` : undefined}
    >
      <div className="cl-kpi-body">
        <span className="cl-kpi-icon" style={{ background: bg, color: cor }}>
          <Icon n={icon} s={19} sw={1.7} />
        </span>
        <div className="cl-kpi-text">
          <div className="cl-kpi-label">{label}</div>
          <div className="cl-kpi-value">{value}</div>
        </div>
      </div>
      <div className={`cl-kpi-foot${footTone ? ` ${footTone}` : ''}`}>{foot}</div>
    </Tag>
  )
}

function Avatar({ id, nome, ativo }) {
  return (
    <span className="cl-avatar-wrap">
      <span className="cl-avatar" style={{ background: corAvatar(id) }}>{iniciais(nome)}</span>
      {ativo && <span className="cl-avatar-dot" />}
    </span>
  )
}

function Donut({ fatias, total }) {
  const R = 15.915494
  let acumulado = 0
  return (
    <div className="cl-donut">
      <svg viewBox="0 0 42 42">
        <circle cx="21" cy="21" r={R} fill="none" stroke="#f2f4f7" strokeWidth="7.2" />
        {fatias.map((f) => {
          const pct = total ? (f.valor / total) * 100 : 0
          if (pct <= 0) return null
          const dash = Math.max(pct - 0.8, 0.6)
          const el = (
            <circle
              key={f.id}
              cx="21" cy="21" r={R} fill="none"
              stroke={f.cor} strokeWidth="7.2"
              strokeDasharray={`${dash} ${100 - dash}`}
              strokeDashoffset={-acumulado}
            />
          )
          acumulado += pct
          return el
        })}
      </svg>
      <div className="cl-donut-center">
        <div className="cl-donut-total">{total}</div>
        <div className="cl-donut-label">leads</div>
      </div>
    </div>
  )
}

function RowMenu({ aberto, onToggle, onFechar, onPerfil, onEditar, onNegocio }) {
  const ref = useRef(null)
  const btnRef = useRef(null)
  const [pos, setPos] = useState(null)

  useEffect(() => {
    if (!aberto) { setPos(null); return undefined }
    const ancorar = () => {
      const rect = btnRef.current?.getBoundingClientRect()
      if (!rect) return
      const acima = window.innerHeight - rect.bottom < 180
      setPos({
        top: acima ? undefined : rect.bottom + 4,
        bottom: acima ? window.innerHeight - rect.top + 4 : undefined,
        right: window.innerWidth - rect.right,
      })
    }
    ancorar()
    const fora = (e) => {
      if (ref.current?.contains(e.target) || btnRef.current?.contains(e.target)) return
      onFechar()
    }
    const esc = (e) => { if (e.key === 'Escape') onFechar() }
    document.addEventListener('mousedown', fora)
    document.addEventListener('keydown', esc)
    window.addEventListener('scroll', ancorar, true)
    window.addEventListener('resize', ancorar)
    return () => {
      document.removeEventListener('mousedown', fora)
      document.removeEventListener('keydown', esc)
      window.removeEventListener('scroll', ancorar, true)
      window.removeEventListener('resize', ancorar)
    }
  }, [aberto, onFechar])

  const item = (acao) => (e) => { e.stopPropagation(); onFechar(); acao() }

  return (
    <>
      <button ref={btnRef} type="button" className="cl-icon-btn" title="Mais ações"
        onClick={(e) => { e.stopPropagation(); onToggle() }}>
        <Icon n="more" s={16} />
      </button>
      {aberto && pos && (
        <div ref={ref} className="cl-row-menu" style={pos} onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={item(onPerfil)}><Icon n="eye" s={15} /> Ver perfil</button>
          <button type="button" onClick={item(onEditar)}><Icon n="edit" s={15} /> Editar lead</button>
          <button type="button" onClick={item(onNegocio)}><Icon n="sales" s={15} /> Novo negócio</button>
        </div>
      )}
    </>
  )
}

// ── Página ──────────────────────────────────────────────────────────────────

export default function Leads() {
  const [contacts, setContacts] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')          // '' | ativo | inativo
  const [responsavel, setResponsavel] = useState('')
  const [saudeFiltro, setSaudeFiltro] = useState('')
  const [origemFiltro, setOrigemFiltro] = useState('')
  const [formFiltro, setFormFiltro] = useState('')      // form_id do Lead Ads (#306)
  const [view, setView] = useState('tabela')        // tabela | cards
  const [sort, setSort] = useState({ key: 'interacao', dir: 'desc' })
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [sideOpen, setSideOpen] = useState(true)
  const [menuId, setMenuId] = useState(null)

  const [modal, setModal] = useState(null)          // ClienteFormPanel (editar): {contactId}
  const [newModal, setNewModal] = useState(false)   // ClienteFormPanel (novo lead)
  const [camposOpen, setCamposOpen] = useState(false) // campos personalizados do lead (#306)
  const [dealModal, setDealModal] = useState(null)

  const navigate = useNavigate()
  const verPerfil = (id) => navigate(`/comercial/leads/${id}`)

  const { selectedClient } = useApp()
  const clientId = selectedClient?.id || null
  const hasClient = !!clientId

  const load = useCallback(async () => {
    if (!clientId) { setContacts([]); setMembers([]); setLoading(false); return }
    setLoading(true); setErr(null)
    try {
      const [rows, mbs] = await Promise.all([
        getCrmContacts(),
        getCrmMembers().catch(() => []),
      ])
      setContacts(rows || [])
      setMembers(Array.isArray(mbs) ? mbs : [])
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    setModal(null); setNewModal(false); setDealModal(null)
    setSearch(''); setStatus(''); setResponsavel(''); setSaudeFiltro(''); setOrigemFiltro(''); setPage(1)
    load()
  }, [load])

  // "Novo negócio" a partir do lead: DealModal no funil padrão, 1ª etapa, com
  // o lead pré-anexado (mesmo fluxo usado em Clientes).
  const handleNewDeal = useCallback(async (contact) => {
    if (!contact) return
    let pipelineId = null, stageId = null
    try {
      const pls = await getPipelines()
      const def = pls.find((p) => p.is_default) || pls[0]
      if (def) {
        pipelineId = def.id
        const stages = await getStages(def.id)
        stageId = stages[0]?.id || null
      }
    } catch { /* segue sem funil; DealModal cria mesmo assim */ }
    setDealModal({ pipelineId, stageId, initialContact: { id: contact.id, nome: contact.nome } })
  }, [])

  const ownerNome = useMemo(() => {
    const mapa = new Map(members.map((m) => [m.id, m.nome]))
    return (ownerId) => mapa.get(ownerId) || null
  }, [members])

  const enriquecidos = useMemo(() => contacts.map((c) => {
    const saude = saudeDe(c)
    return {
      ...c,
      _saude: saude,
      _origem: origemDe(c),
      _owner: ownerNome(c.owner_id),
      _proxima: proximaAcao(c, saude),
    }
  }), [contacts, ownerNome])

  const responsaveis = useMemo(
    () => [...new Set(enriquecidos.map((c) => c._owner).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [enriquecidos],
  )
  const origensDisponiveis = useMemo(
    () => [...new Set(enriquecidos.map((c) => c._origem))].sort((a, b) => a.localeCompare(b)),
    [enriquecidos],
  )

  // Formulários de Lead Ads dos quais já veio lead: [{ id, nome }] por nome.
  const formulariosDisponiveis = useMemo(() => {
    const porId = new Map()
    for (const c of contacts) {
      if (c.form_id && !porId.has(c.form_id)) porId.set(c.form_id, c.form_name || `Formulário ${c.form_id}`)
    }
    return [...porId].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome))
  }, [contacts])

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase()
    let rows = enriquecidos
    if (q) {
      rows = rows.filter((c) => [c.nome, c.empresa, c.company_nome, c._owner, c.email, c.telefone, c.cidade]
        .some((v) => (v || '').toLowerCase().includes(q)))
    }
    if (status) rows = rows.filter((c) => (status === 'ativo' ? c._saude !== 'inativo' : c._saude === 'inativo'))
    if (responsavel) rows = rows.filter((c) => c._owner === responsavel)
    if (saudeFiltro) rows = rows.filter((c) => c._saude === saudeFiltro)
    if (origemFiltro) rows = rows.filter((c) => c._origem === origemFiltro)
    if (formFiltro) rows = rows.filter((c) => c.form_id === formFiltro)

    const get = ORDENACOES[sort.key] || ORDENACOES.interacao
    const mult = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const va = get(a), vb = get(b)
      if (va < vb) return -1 * mult
      if (va > vb) return 1 * mult
      return 0
    })
  }, [enriquecidos, search, status, responsavel, saudeFiltro, origemFiltro, formFiltro, sort])

  const totalPages = Math.max(1, Math.ceil(filtrados.length / perPage))
  const pageAtual = Math.min(page, totalPages)
  const visiveis = filtrados.slice((pageAtual - 1) * perPage, pageAtual * perPage)

  useEffect(() => { setPage(1) }, [search, status, responsavel, saudeFiltro, origemFiltro, formFiltro, perPage])

  // ── Indicadores ───────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const hoje = new Date()
    const mesAtual = { ano: hoje.getFullYear(), mes: hoje.getMonth() }
    const ant = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)
    const mesAnterior = { ano: ant.getFullYear(), mes: ant.getMonth() }
    const criadosNoMes = (alvo) => enriquecidos.filter((c) => {
      const m = mesDe(c.created_at)
      return m && m.ano === alvo.ano && m.mes === alvo.mes
    })

    const atuais = criadosNoMes(mesAtual), anteriores = criadosNoMes(mesAnterior)
    const somar = (lista) => lista.reduce((s, c) => s + num(c.valor_venda), 0)
    const potencialMes = somar(atuais), potencialAnt = somar(anteriores)
    const comValor = enriquecidos.filter((c) => num(c.valor_venda) > 0)
    const ticketMes = atuais.length ? potencialMes / atuais.length : 0
    const ticketAnt = anteriores.length ? potencialAnt / anteriores.length : 0

    const negociacaoAtuais = atuais.filter((c) => c.status === 'negociacao')
    const negociacaoAnt = anteriores.filter((c) => c.status === 'negociacao')
    const emNegociacaoMes = somar(negociacaoAtuais), emNegociacaoAnt = somar(negociacaoAnt)

    const frios = enriquecidos.filter((c) => c._saude === 'risco').length
    const totalLeads = enriquecidos.length

    return {
      ativos: enriquecidos.filter((c) => c._saude !== 'inativo').length,
      novosNoMes: atuais.length,
      potencial: enriquecidos.reduce((s, c) => s + num(c.valor_venda), 0),
      potencialDelta: variacao(potencialMes, potencialAnt),
      ticket: comValor.length ? comValor.reduce((s, c) => s + num(c.valor_venda), 0) / comValor.length : 0,
      ticketDelta: variacao(ticketMes, ticketAnt),
      emNegociacao: enriquecidos.filter((c) => c.status === 'negociacao').reduce((s, c) => s + num(c.valor_venda), 0),
      emNegociacaoDelta: variacao(emNegociacaoMes, emNegociacaoAnt),
      frios,
      friosPct: totalLeads ? (frios / totalLeads) * 100 : 0,
    }
  }, [enriquecidos])

  const pct1 = (v) => `${v > 0 ? '+' : ''}${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
  const rodapeDelta = (v) => (v == null
    ? <>sem base de comparação</>
    : <><b>{pct1(v)}</b> vs. mês anterior</>)
  const tomDelta = (v) => (v == null ? '' : v >= 0 ? 'up' : 'down')

  // ── Visão dos leads (coluna direita) ──────────────────────────────────────
  const distribuicao = useMemo(() => {
    const cont = { saudavel: 0, atencao: 0, risco: 0, inativo: 0 }
    for (const c of enriquecidos) cont[c._saude] += 1
    return SAUDES.map((id) => ({ id, valor: cont[id], cor: SAUDE[id].cor, label: SAUDE[id].label }))
  }, [enriquecidos])

  const origens = useMemo(() => {
    const vazio = () => ({ saudavel: 0, atencao: 0, risco: 0, inativo: 0 })
    const mapa = new Map()
    for (const c of enriquecidos) {
      const seg = c._origem
      if (!mapa.has(seg)) mapa.set(seg, { nome: seg, total: 0, faixas: vazio() })
      const item = mapa.get(seg)
      item.total += 1
      item.faixas[c._saude] += 1
    }
    return [...mapa.values()].sort((a, b) => b.total - a.total).slice(0, 5)
  }, [enriquecidos])

  const maiorOrigem = origens[0]?.total || 1

  const precisamAtencao = useMemo(() => enriquecidos
    .filter((c) => c._saude === 'risco' || c._saude === 'inativo')
    .sort((a, b) => new Date(a.updated_at || 0) - new Date(b.updated_at || 0))
    .slice(0, 3), [enriquecidos])

  // ── Exportar CSV do que está filtrado ─────────────────────────────────────
  const exportar = () => {
    const cab = ['Lead', 'Empresa', 'Responsável', 'Saúde', 'Origem', 'Valor potencial', 'Última interação', 'Próxima ação']
    const linhas = filtrados.map((c) => [
      c.nome || '', c.company_nome || c.empresa || '', c._owner || '',
      SAUDE[c._saude].label, c._origem,
      String(num(c.valor_venda)).replace('.', ','),
      fmtDate(c.updated_at), `${fmtDate(c._proxima.data)} ${c._proxima.label}`,
    ])
    const csv = [cab, ...linhas]
      .map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n')
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const ordenar = (key) => setSort((s) => (
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'nome' || key === 'empresa' || key === 'responsavel' ? 'asc' : 'desc' }
  ))

  const th = (label, sortKey) => (
    <th key={sortKey}>
      <button type="button" className="cl-th-sort" onClick={() => ordenar(sortKey)}>
        {label}
        {sort.key === sortKey && <Icon n={sort.dir === 'asc' ? 'chevron_u' : 'chevron_d'} s={12} c="#98a2b3" />}
      </button>
    </th>
  )

  const rodape = () => (
    <div className="cl-foot">
      <span className="cl-foot-info">
        Mostrando {(pageAtual - 1) * perPage + 1} a {Math.min(pageAtual * perPage, filtrados.length)} de {filtrados.length} leads
      </span>
      <div className="cl-pages">
        <button type="button" className="cl-page" disabled={pageAtual <= 1}
          onClick={() => setPage(pageAtual - 1)} aria-label="Página anterior">
          <Icon n="chevron_l" s={15} />
        </button>
        {listaPaginas(pageAtual, totalPages).map((p, i) => (
          p === '…'
            ? <span key={`gap${i}`} className="cl-page-gap">…</span>
            : (
              <button key={p} type="button"
                className={`cl-page${p === pageAtual ? ' active' : ''}`}
                onClick={() => setPage(p)}>
                {p}
              </button>
            )
        ))}
        <button type="button" className="cl-page" disabled={pageAtual >= totalPages}
          onClick={() => setPage(pageAtual + 1)} aria-label="Próxima página">
          <Icon n="chevron_r" s={15} />
        </button>
      </div>
      <select className="cl-select" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
        {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} por página</option>)}
      </select>
    </div>
  )

  const contato = (c) => {
    if (c.telefone) return `https://wa.me/${String(c.telefone).replace(/\D/g, '')}`
    if (c.email) return `mailto:${c.email}`
    return null
  }

  if (!hasClient) {
    return (
      <Layout title="Leads" breadcrumb={[{ label: 'Comercial' }, { label: 'Leads' }]}>
        <div className="cl-state">Selecione um cliente para ver os leads.</div>
      </Layout>
    )
  }

  return (
    <Layout title="Leads" breadcrumb={[{ label: 'Comercial' }, { label: 'Leads' }]}>
      {/* ── Indicadores + novo lead ─────────────────────────────────────────── */}
      <div className="cl-top">
        <div className="cl-kpis">
          <Kpi
            icon="users" cor="#2563eb" bg="#eaf0fe" label="Leads ativos"
            value={kpis.ativos}
            foot={<><b>+{kpis.novosNoMes}</b> este mês</>} footTone="up"
            active={status === 'ativo'}
            onClick={() => setStatus((s) => (s === 'ativo' ? '' : 'ativo'))}
          />
          <Kpi
            icon="currency" cor="#12a06a" bg="#e6f8f0" label="Valor potencial"
            value={brl0(kpis.potencial)}
            foot={rodapeDelta(kpis.potencialDelta)} footTone={tomDelta(kpis.potencialDelta)}
          />
          <Kpi
            icon="profile" cor="#7c5cf0" bg="#f0edfe" label="Ticket médio"
            value={brl0(kpis.ticket)}
            foot={rodapeDelta(kpis.ticketDelta)} footTone={tomDelta(kpis.ticketDelta)}
          />
          <Kpi
            icon="chart_up" cor="#f0a125" bg="#fef4e2" label="Em negociação"
            value={brl0(kpis.emNegociacao)}
            foot={rodapeDelta(kpis.emNegociacaoDelta)} footTone={tomDelta(kpis.emNegociacaoDelta)}
          />
          <Kpi
            icon="alert" cor="#ef4b45" bg="#fdeeed" label="Leads frios"
            value={kpis.frios}
            foot={<><b>{kpis.friosPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%</b> do total</>}
            footTone="risk"
            active={saudeFiltro === 'risco'}
            onClick={() => setSaudeFiltro((s) => (s === 'risco' ? '' : 'risco'))}
          />
        </div>
        <div className="cl-actions">
          <button type="button" className="cl-new" onClick={() => setNewModal(true)}>
            <Icon n="plus" s={16} sw={2.2} /> Novo lead
          </button>
          <button type="button" className="cl-new cl-new-ghost" onClick={() => setCamposOpen(true)}>
            <Icon n="settings" s={16} /> Campos
          </button>
        </div>
      </div>

      {err && <div className="cl-error">{err}</div>}

      <div className="cl-grid">
        {/* ── Lista ────────────────────────────────────────────────────────── */}
        <div className="cl-card">
          <div className="cl-filters">
            <div className="cl-search">
              <span className="cl-search-icon"><Icon n="search" s={16} /></span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por lead, empresa ou responsável..."
              />
            </div>

            <label className="cl-field">
              <span className="cl-field-label">Status</span>
              <select className="cl-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Todos</option>
                <option value="ativo">Ativos</option>
                <option value="inativo">Inativos</option>
              </select>
            </label>

            <label className="cl-field">
              <span className="cl-field-label">Responsável</span>
              <select className="cl-select" value={responsavel} onChange={(e) => setResponsavel(e.target.value)}>
                <option value="">Todos</option>
                {responsaveis.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>

            <label className="cl-field">
              <span className="cl-field-label">Saúde</span>
              <select className="cl-select" value={saudeFiltro} onChange={(e) => setSaudeFiltro(e.target.value)}>
                <option value="">Todos</option>
                {SAUDES.map((s) => <option key={s} value={s}>{SAUDE[s].label}</option>)}
              </select>
            </label>

            <label className="cl-field">
              <span className="cl-field-label">Origem</span>
              <select className="cl-select" value={origemFiltro} onChange={(e) => setOrigemFiltro(e.target.value)}>
                <option value="">Selecione</option>
                {origensDisponiveis.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>

            {formulariosDisponiveis.length > 0 && (
              <label className="cl-field">
                <span className="cl-field-label">Formulário</span>
                <select className="cl-select" value={formFiltro} onChange={(e) => setFormFiltro(e.target.value)}>
                  <option value="">Todos</option>
                  {formulariosDisponiveis.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
              </label>
            )}

            <div className="cl-filters-right">
              <div className="cl-views">
                <button type="button" className={`cl-view${view === 'tabela' ? ' active' : ''}`}
                  onClick={() => setView('tabela')}>
                  <Icon n="grid" s={15} /> Tabela
                </button>
                <button type="button" className={`cl-view${view === 'cards' ? ' active' : ''}`}
                  onClick={() => setView('cards')}>
                  <Icon n="folder" s={15} /> Cards
                </button>
              </div>
              <button type="button" className="cl-export" onClick={exportar}>
                <Icon n="upload" s={15} c="#667085" /> Exportar
              </button>
            </div>
          </div>

          {loading ? (
            <div className="cl-state">Carregando…</div>
          ) : !filtrados.length ? (
            <div className="cl-state">
              {contacts.length
                ? 'Nenhum lead corresponde aos filtros.'
                : 'Nenhum lead cadastrado ainda.'}
            </div>
          ) : view === 'tabela' ? (
            <>
              <div className="cl-table-scroll">
                <table className="cl-table">
                  <thead>
                    <tr>
                      {th('Lead', 'nome')}
                      {th('Empresa', 'empresa')}
                      {th('Responsável', 'responsavel')}
                      {th('Saúde', 'saude')}
                      <th>Origem</th>
                      {th('Valor potencial', 'valor')}
                      {th('Última interação', 'interacao')}
                      <th>Próxima ação</th>
                      <th className="cl-col-acoes" aria-label="Ações" />
                    </tr>
                  </thead>
                  <tbody>
                    {visiveis.map((c) => {
                      const link = contato(c)
                      return (
                        <tr
                          key={c.id}
                          onClick={() => verPerfil(c.id)}
                        >
                          <td>
                            <div className="cl-client">
                              <Avatar id={c.id} nome={c.nome} ativo={c._saude !== 'inativo'} />
                              <span className="cl-client-name">{c.nome || 'Sem nome'}</span>
                            </div>
                          </td>
                          <td>
                            <div className="cl-empresa" title={c.company_nome || c.empresa || ''}>
                              {c.company_nome || c.empresa || '—'}
                            </div>
                          </td>
                          <td>
                            {c._owner ? (
                              <div className="cl-owner">
                                <span className="cl-owner-avatar">{iniciais(c._owner)}</span>
                                <span className="cl-owner-name" title={c._owner}>{c._owner}</span>
                              </div>
                            ) : <span className="cl-proj-empty">—</span>}
                          </td>
                          <td>
                            <span className={`cl-pill ${c._saude}`}>{SAUDE[c._saude].label}</span>
                          </td>
                          <td>
                            <span className="cl-proj-empty">{c._origem}</span>
                          </td>
                          <td className="cl-receita">{brl0(c.valor_venda)}</td>
                          <td>
                            <div className="cl-stack-top">{fmtDate(c.updated_at)}</div>
                            <div className="cl-stack-sub">{desde(c.updated_at)}</div>
                          </td>
                          <td>
                            <div className="cl-stack-top">{fmtDate(c._proxima.data)}</div>
                            <div className="cl-stack-sub">{c._proxima.label}</div>
                          </td>
                          <td className="cl-col-acoes">
                            <div className="cl-row-actions">
                              <a
                                className="cl-icon-btn"
                                href={link || undefined}
                                target="_blank"
                                rel="noreferrer"
                                title={c.telefone ? 'Falar no WhatsApp' : c.email ? 'Enviar e-mail' : 'Sem contato cadastrado'}
                                onClick={(e) => { e.stopPropagation(); if (!link) e.preventDefault() }}
                                style={link ? undefined : { opacity: .45, pointerEvents: 'none' }}
                              >
                                <Icon n="attend" s={16} />
                              </a>
                              <button type="button" className="cl-icon-btn" title="Ver perfil"
                                onClick={(e) => { e.stopPropagation(); verPerfil(c.id) }}>
                                <Icon n="eye" s={16} />
                              </button>
                              <RowMenu
                                aberto={menuId === c.id}
                                onToggle={() => setMenuId((id) => (id === c.id ? null : c.id))}
                                onFechar={() => setMenuId(null)}
                                onPerfil={() => verPerfil(c.id)}
                                onEditar={() => setModal({ contactId: c.id })}
                                onNegocio={() => handleNewDeal(c)}
                              />
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {rodape()}
            </>
          ) : (
            <>
              <div className="cl-cards">
                {visiveis.map((c) => (
                  <button key={c.id} type="button" className="cl-cardcli" onClick={() => verPerfil(c.id)}>
                    <div className="cl-cardcli-head">
                      <Avatar id={c.id} nome={c.nome} ativo={c._saude !== 'inativo'} />
                      <div className="cl-cardcli-meta">
                        <div className="cl-client-name">{c.nome || 'Sem nome'}</div>
                        <div className="cl-cardcli-sub">{c.company_nome || c.empresa || 'Sem empresa'}</div>
                      </div>
                      <span className={`cl-pill ${c._saude}`}>{SAUDE[c._saude].label}</span>
                    </div>
                    <div className="cl-cardcli-row">
                      <span>Valor potencial</span><b>{brl0(c.valor_venda)}</b>
                    </div>
                    <div className="cl-cardcli-row">
                      <span>Responsável</span><b>{c._owner || '—'}</b>
                    </div>
                    <div className="cl-cardcli-row">
                      <span>Origem</span><b>{c._origem}</b>
                    </div>
                    <div className="cl-cardcli-row">
                      <span>Próxima ação</span><b>{fmtDate(c._proxima.data)} · {c._proxima.label}</b>
                    </div>
                  </button>
                ))}
              </div>

              {rodape()}
            </>
          )}
        </div>

        {/* ── Visão dos leads ──────────────────────────────────────────────── */}
        <aside className="cl-side">
          <div className="cl-side-head">
            <span className="cl-side-title">Visão dos leads</span>
            <button type="button" className="cl-side-toggle" onClick={() => setSideOpen((o) => !o)}
              aria-label={sideOpen ? 'Recolher' : 'Expandir'}>
              <Icon n={sideOpen ? 'chevron_u' : 'chevron_d'} s={16} />
            </button>
          </div>

          {sideOpen && (
            <div className="cl-side-body">
              <div className="cl-panel">
                <div className="cl-panel-title">Distribuição por saúde</div>
                <div className="cl-donut-wrap">
                  <Donut fatias={distribuicao} total={enriquecidos.length} />
                  <div className="cl-legend">
                    {distribuicao.map((f) => (
                      <div key={f.id} className="cl-legend-row">
                        <span className="cl-legend-dot" style={{ background: f.cor }} />
                        <span className="cl-legend-name">{f.label}</span>
                        <span className="cl-legend-val">
                          {f.valor} ({enriquecidos.length
                            ? ((f.valor / enriquecidos.length) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
                            : '0,0'}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="cl-panel">
                <div className="cl-panel-title">Leads por origem</div>
                {origens.length ? origens.map((s) => (
                  <div key={s.nome} className="cl-seg">
                    <span className="cl-seg-name" title={s.nome}>{s.nome}</span>
                    <span className="cl-seg-track">
                      <span className="cl-seg-bar" style={{ width: `${(s.total / maiorOrigem) * 100}%` }}>
                        {FAIXAS_ORIGEM.map(([k, cor]) => (
                          s.faixas[k] > 0 && (
                            <span key={k} style={{ width: `${(s.faixas[k] / s.total) * 100}%`, background: cor }} />
                          )
                        ))}
                      </span>
                    </span>
                    <span className="cl-seg-val">{s.total}</span>
                  </div>
                )) : <div className="cl-empty-side">Sem leads cadastrados.</div>}
              </div>

              <div className="cl-panel">
                <div className="cl-panel-title">Precisam de atenção</div>
                {precisamAtencao.length ? precisamAtencao.map((c) => (
                  <button key={c.id} type="button" className="cl-attn" onClick={() => verPerfil(c.id)}>
                    <Avatar id={c.id} nome={c.nome} ativo={false} />
                    <span className="cl-attn-info">
                      <span className="cl-attn-name">{c.nome || 'Sem nome'}</span>
                      <span className="cl-attn-why">{motivoAtencao(c, c._saude)}</span>
                    </span>
                    <span className="cl-attn-icon"><Icon n="alert" s={17} /></span>
                  </button>
                )) : <div className="cl-empty-side">Nenhum lead frio no momento.</div>}

                <button type="button" className="cl-report"
                  onClick={() => { setSaudeFiltro('risco'); setView('tabela'); setPage(1) }}>
                  <Icon n="chart" s={15} /> Ver relatório completo
                </button>
              </div>
            </div>
          )}
        </aside>
      </div>

      {modal && (
        <ClienteFormPanel
          contactId={modal.contactId || null}
          entityLabel="lead"
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }}
        />
      )}

      <LeadCustomFieldsManager open={camposOpen} onClose={() => setCamposOpen(false)} />

      {newModal && (
        <ClienteFormPanel
          contactId={null}
          initialStatus="prospect"
          entityLabel="lead"
          onClose={() => setNewModal(false)}
          onCreatedDeal={(c) => handleNewDeal(c)}
          onSaved={() => { setNewModal(false); load() }}
        />
      )}

      {dealModal && (
        <DealModal
          dealId={null}
          pipelineId={dealModal.pipelineId}
          stageId={dealModal.stageId}
          initialContact={dealModal.initialContact}
          onClose={() => setDealModal(null)}
          onSaved={() => { setDealModal(null); load() }}
        />
      )}
    </Layout>
  )
}
